import { Link } from "@inertiajs/react"
import { Pencil } from "lucide-react"

import EngagementMetrics from "@/components/engagement-metrics"
import PlatformIcon from "@/components/icons/platform-icon"
import {
  PlatformStatusCheckMark,
  PlatformStatusXMark,
} from "@/components/icons/platform-status-mark"
import { Badge } from "@/components/ui/badge"
import { useTimeZone } from "@/hooks/use-time-zone"
import { formatDate } from "@/lib/format"
import { editStoryPath, storyPath } from "@/routes"
import type { Story } from "@/types"

function isVideoContentType(contentType?: string | null) {
  return contentType?.startsWith("video/") ?? false
}

interface Props {
  story: Story
  platformLabels: Record<string, string>
}

export default function StoryCard({ story, platformLabels }: Props) {
  const timeZone = useTimeZone()
  const mediaFile = story.media_files.find((f) => f.url)

  return (
    <div className="hover:bg-accent relative flex h-full gap-3 rounded-lg border p-4 transition-colors">
      {mediaFile && (
        <div className="shrink-0">
          {isVideoContentType(mediaFile.content_type) ? (
            <video
              src={mediaFile.url!}
              className="h-16 w-16 rounded object-cover"
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={mediaFile.url!}
              alt={mediaFile.alt_text ?? ""}
              className="h-16 w-16 rounded object-cover"
            />
          )}
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-muted-foreground line-clamp-3 flex-1 text-sm wrap-anywhere">
          {story.body ? (
            <span>{story.body}</span>
          ) : (
            <span className="italic">No content yet</span>
          )}
        </p>
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={story.long_form ? "secondary" : "outline"}
              className="text-xs"
            >
              {story.long_form ? "Long form" : "Short form"}
            </Badge>
            {!story.long_form && story.take_count > 1 && (
              <span className="text-muted-foreground text-xs font-medium">
                {story.take_count} takes
              </span>
            )}
            <EngagementMetrics
              likes={story.total_likes}
              views={story.total_views}
              reposts={story.total_reposts}
              shares={story.total_shares}
              quotes={story.total_quotes}
              size="sm"
            />
            {story.scheduled && story.scheduled_at ? (
              <span className="text-muted-foreground text-xs">
                Scheduled {formatDate(story.scheduled_at, true, timeZone)}
              </span>
            ) : (
              <span className="text-muted-foreground text-xs">
                {formatDate(story.last_published_at ?? story.updated_at)}
              </span>
            )}
          </div>
          {story.syndications.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {story.syndications.map((s) => {
                const label = platformLabels[s.platform.name] ?? s.platform.name
                return (
                  <span
                    key={s.id}
                    aria-label={label}
                    title={label}
                    className="flex items-center gap-0.5"
                  >
                    <PlatformIcon
                      platform={s.platform.name}
                      className="text-muted-foreground size-3.5"
                    />
                    {s.status === "success" && <PlatformStatusCheckMark />}
                    {s.status === "failed" && <PlatformStatusXMark />}
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </div>
      <Link
        href={storyPath(story.id)}
        aria-label="View story"
        className="absolute inset-0 rounded-lg"
      />
      <Link
        href={editStoryPath(story.id)}
        aria-label="Edit story"
        className="text-muted-foreground hover:bg-background hover:text-foreground relative z-10 flex size-8 shrink-0 items-center justify-center self-start rounded"
      >
        <Pencil className="size-4" />
      </Link>
    </div>
  )
}
